import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useLanguage } from './LanguageContext';
import { buildWhatsAppLink, contactEmail, contactPhone } from './siteContent';

const NAV_ITEMS = [
  { to: '/about',    key: 'about',    icon: 'person' },
  { to: '/services', key: 'services', icon: 'design_services' },
  { to: '/price',    key: 'team',     icon: 'sell' },
  { to: '/work',     key: 'works',    icon: 'collections_bookmark' },
  { to: '/contact',  key: 'contact',  icon: 'mail' },
];

export function TopNav() {
  const { content, toggleLanguage, language } = useLanguage();
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => { setMenuOpen(false); }, [location]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  return (
    <>
      <nav className="navbar">
        <div className="nav-container">
          <Link className="nav-logo" to="/">
            <img src="/digitara.PNG" alt="DIGITARA" className="nav-logo-img" />
          </Link>

          {/* Desktop Menu */}
          <div className="nav-desktop-menu">
            <div className="nav-links">
              {NAV_ITEMS.map(({ to, key }) => (
                <NavLink key={to} className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'} to={to}>
                  {content.nav[key]}
                </NavLink>
              ))}
            </div>
            <div className="nav-actions">
              <button aria-label={content.nav.languageLabel} className="lang-toggle" onClick={toggleLanguage} type="button">
                {language === 'id' ? 'EN' : 'ID'}
              </button>
              <Link to="/contact" className="nav-btn">
                {content.nav.getStarted}
                <span aria-hidden="true" className="material-symbols-outlined">arrow_forward</span>
              </Link>
            </div>
          </div>

          {/* Hamburger */}
          <button
            className={`mobile-menu-btn${menuOpen ? ' is-open' : ''}`}
            type="button"
            aria-label="Toggle menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen(p => !p)}
          >
            <span className="hbg-bars" aria-hidden="true">
              <span className="hbg-bar" />
              <span className="hbg-bar" />
              <span className="hbg-bar" />
            </span>
          </button>
        </div>
      </nav>

      {/* Backdrop */}
      <div
        className={`mobile-backdrop${menuOpen ? ' active' : ''}`}
        aria-hidden="true"
        onClick={() => setMenuOpen(false)}
      />

      {/* Drawer */}
      <div className={`mobile-drawer${menuOpen ? ' active' : ''}`} aria-hidden={!menuOpen} role="dialog" aria-label="Menu">
        {/* Drawer Header */}
        <div className="drawer-header">
          <Link className="drawer-logo" to="/" onClick={() => setMenuOpen(false)}>
            <img src="/digitara.PNG" alt="DIGITARA" className="drawer-logo-img" />
          </Link>
          <button className="drawer-close" onClick={() => setMenuOpen(false)} aria-label="Tutup menu" type="button">
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        {/* Accent line */}
        <div className="drawer-accent" aria-hidden="true" />

        {/* Nav Links */}
        <nav className="drawer-nav">
          {NAV_ITEMS.map(({ to, key, icon }) => (
            <NavLink
              key={to}
              className={({ isActive }) => `drawer-link${isActive ? ' active' : ''}`}
              to={to}
              onClick={() => setMenuOpen(false)}
            >
              <span className="drawer-link-icon material-symbols-outlined" aria-hidden="true">{icon}</span>
              <span className="drawer-link-text">{content.nav[key]}</span>
              <span className="drawer-link-arrow material-symbols-outlined" aria-hidden="true">chevron_right</span>
            </NavLink>
          ))}
        </nav>

        {/* Footer */}
        <div className="drawer-footer">
          <button className="drawer-lang" onClick={toggleLanguage} type="button">
            <span className="material-symbols-outlined">language</span>
            {language === 'id' ? 'Switch to English' : 'Ganti ke Indonesia'}
          </button>
          <Link to="/contact" className="drawer-cta" onClick={() => setMenuOpen(false)}>
            {content.nav.getStarted}
            <span className="material-symbols-outlined" aria-hidden="true">arrow_forward</span>
          </Link>
        </div>
      </div>
    </>
  );
}

/* ── WhatsApp Float Button ─────────────────────────────── */
export function WhatsAppFloat() {
  const waUrl = 'https://wa.me/6281585020920?text=Halo%20DIGITARA%2C%20saya%20ingin%20konsultasi.';
  return (
    <a href={waUrl} target="_blank" rel="noopener noreferrer" className="wa-float" aria-label="Chat via WhatsApp">
      <span className="wa-float-ring" aria-hidden="true" />
      <svg className="wa-float-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
      </svg>
      <span className="wa-float-tooltip" aria-hidden="true">Chat WhatsApp</span>
    </a>
  );
}

/* ── Site Footer ───────────────────────────────────────── */
export function SiteFooter() {
  const { content } = useLanguage();
  return (
    <footer className="footer" style={{ position: 'relative', overflow: 'hidden' }}>
      <div className="footer-container footer-grid" style={{ position: 'relative', zIndex: 1 }}>
        <div className="footer-brand">
          <img src="/digitara.PNG" alt="DIGITARA" className="footer-logo-img" />
          <p className="footer-tagline">{content.footer.tagline}</p>
          <div className="footer-copyright-inline">{content.footer.copyright}</div>
        </div>
        <div className="footer-contact">
          <div className="footer-section-title">{content.footer.contact}</div>
          <a className="footer-contact-link" href="https://instagram.com/official_digitara" target="_blank" rel="noreferrer">
            <span aria-hidden="true" className="material-symbols-outlined">photo_camera</span>
            <span>@official_digitara</span>
          </a>
          <a className="footer-contact-link" href={buildWhatsAppLink('Halo DIGITARA, saya ingin konsultasi.')} target="_blank" rel="noreferrer">
            <span aria-hidden="true" className="material-symbols-outlined">chat</span>
            <span>{contactPhone}</span>
          </a>
          <a className="footer-contact-link" href={`mailto:${contactEmail}`}>
            <span aria-hidden="true" className="material-symbols-outlined">mail</span>
            <span>{contactEmail}</span>
          </a>
        </div>
      </div>
    </footer>
  );
}
